
const libPictProvider = require('pict-provider');
const libPictRouter = require('pict-router');

class LIMSRouter extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.currentView = 'unknown';
		this.currentSample = null;
		this.currentDocument = null;

		this.defaultRoute = 'PSRS/Sample/List/FilteredTo/FSF~CreateDate~DESC~0';
		// We are using this state so that after relogin we go back to the last known route, cleanly.
		this.cachedLogoutRoute = false;
		/** @type {import('../LIMS-Application-Web.js').LIMSPict} */
		this.pict;
		if (!('PictRouter' in this.pict.providers))
		{
			this.pict.addProvider('PictRouter', { RouterMode: 'memory' }, libPictRouter);
		}
		this.router.hooks(
		{
			before: async (next, params) =>
			{
				if (params?.url == 'Logout')
				{
					return next();
				}
				if (!this.AppData.CurrentUser)
				{
					await new Promise((resolve, reject) =>
					{
						this.pict.RestClient.getJSON('/1.0/CheckSession', (pError, pResponse, pBody) =>
						{
							if (pBody.LoggedIn)
							{
								this.AppData.CurrentUser = pBody;
							}
							resolve();
						});
					});
				}
				this.pict.PictApplication.PermissionsControl.setSession(this.AppData.CurrentUser);
				if (params?.url?.includes?.('/SpecificDashboard/'))
				{
					await this.pict.services.ManifestPackagingService.dynamicDashboardLoad(params.data.RecordSet, params.data.DashboardHash);
				}
				// Always apply the sorting parameter for sample lists even while filtering and searching.
				if (params?.url?.startsWith?.('PSRS/Sample/List/FilterExperience/'))
				{
					this.router.navigate(params.url.replace('FilterExperience', 'FilteredTo/FSF~CreateDate~DESC~0/FilterExperience'));
					return next(false);
				}
				else if (params?.url?.startsWith?.('PSRS/Sample/List/FilteredTo/') && !params?.url?.includes('FSF~CreateDate~DESC~0'))
				{
					this.router.navigate(params.url.replace('FilteredTo/', 'FilteredTo/FSF~CreateDate~DESC~0~'));
					return next(false);
				}
				this.pict.log.info(`Route transition before hook`, { params });
				if (this.pict.AppData.JumbotronShown)
				{
					this.pict.PictApplication.hideJumbotron();
				}
				else
				{
					this.pict.PictApplication.showJumbotron();
					this.pict.AppData.JumbotronShown = true;
				}
				next();
			},
		});
	}

	get pictRouter()
	{
		return this.pict.providers.PictRouter;
	}

	get router()
	{
		return this.pictRouter.router;
	}

	onBeforeLoadDataAsync(fCallback)
	{
		super.onBeforeLoadDataAsync((pError) =>
		{
			/** [ Application State Management Routes ]
			 *
			 * These routes are used to manage the state of the application.
			 *
			 */

			// TODO: Once we have a stable, clean pattern shift this to the base pict router class
			this.pictRouter.addRoute('/',
			(pRouteData) =>
			{
				if (this.pict.PictApplication.loggedIn)
				{
					if (this.cachedLogoutRoute)
					{
						const tmpCachedRoute = this.cachedLogoutRoute;
						this.cachedLogoutRoute = false;
						this.router.navigate(tmpCachedRoute);
					}
					else
					{
						this.router.navigate(this.defaultRoute);
					}
				}
				else
				{
					this.pict.views.Empty.renderAsync();
				}
			});

			this.pictRouter.addRoute('/Logout',
			(pRouteData) =>
			{
				// This route is just a placeholder to cache the previous route, so login takes you fluidly back to the correct route.
				const tmpCurrentRoute = this.router.lastResolved();
				if (!tmpCurrentRoute)
				{
					if (this.pict.PictApplication.loggedIn)
					{
						this.router.navigate(this.defaultRoute);
					}
					else
					{
						this.router.navigate('/');
					}
				}
				else if (tmpCurrentRoute.length > 0)
				{
					// Cache the last route so we can go back to it after relogin
					this.cachedLogoutRoute = tmpCurrentRoute[0].hashString;
				}
				else
				{
					this.cachedLogoutRoute = false;
				}
			});

			this.pictRouter.addRoute('/Create-Sample',
			(pRouteData) =>
			{
				this.pict.log.info(`Samples Create route called`);

				this.currentView = 'Create-Sample';
				this.pict.PictApplication.changeTheme('Create');
				this.pict.views.LIMSSampleRecordCreate.onNavigate();
			});

			this.pictRouter.addRoute('/Create-Sample/ClonedFrom/:GUIDSample',
			(pRouteData) =>
			{
				this.pict.log.info(`Samples Create route called`);

				this.currentView = 'Create-Sample';
				this.pict.PictApplication.changeTheme('Create');
				this.pict.views.LIMSSampleRecordCreate.onNavigate(true, pRouteData.data.GUIDSample);
			});

			this.pictRouter.addRoute('/Create-Location',
			(pRouteData) =>
			{
				this.pict.log.info(`Location Create route called`);

				this.currentView = 'Create-Location';
				this.pict.views.LIMSLocationRecordCreate.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Location Create view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Location Create view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Sample/ID/:SampleID',
			(pRouteData) =>
			{
				this.pict.log.info(`Samples ID route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				//TODO: load the sample from the ID
				this.pict.HeadlightRestClient.getJSON(`Sample/${pRouteData.data.SampleID}`, (pError, pRecord) =>
				{
					if (pError)
					{
						//FIXME: toast? user visible error
						this.pict.log.error(`Error rendering Samples ID redirect: ${pError}`);
					}
					else
					{
						this.currentView = 'Sample/' + pRecord.GUIDSample;
						this.pict.PictApplication.changeTheme('Read');
						this.pict.views.LIMSSampleRecordView.onNavigate(pRecord.GUIDSample);
					}
				});
			});

			this.pictRouter.addRoute('/Sample/:SampleGUID',
			(pRouteData) =>
			{
				this.pict.log.info(`Sample Read route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Sample/' + pRouteData.data.SampleGUID;
				this.pict.PictApplication.changeTheme('Read');
				this.pict.views.LIMSSampleRecordView.onNavigate(pRouteData.data.SampleGUID);
			});

			this.pictRouter.addRoute('/Location/:LocationGUID',
			(pRouteData) =>
			{
				this.pict.log.info(`Location Read route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Location/' + pRouteData.data.LocationGUID;
				this.pict.views.LIMSLocationRecordView.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Location Read view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Location Read view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Location/:LocationGUID/Edit',
			(pRouteData) =>
			{
				this.pict.log.info(`Location Edit route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Location/' + pRouteData.data.LocationGUID + '/Edit';
				this.pict.views.LIMSLocationRecordEdit.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Location Edit view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Location Edit view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Document/:DocumentGUID',
			(pRouteData) =>
			{
				this.pict.log.info(`Document Read route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Document/' + pRouteData.data.DocumentGUID;
				this.pict.views.LIMSDocumentRecordView.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Document Read view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Document Read view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Document/:DocumentGUID/Edit',
			(pRouteData) =>
			{
				this.pict.log.info(`Document Edit route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Document/' + pRouteData.data.DocumentGUID + '/Edit';
				this.pict.views.LIMSDocumentRecordEdit.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Document Edit view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Document Edit view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Create-Document',
			(pRouteData) =>
			{
				this.pict.log.info(`Document Create route called`);

				this.currentView = 'Create-Document';
				this.pict.views.LIMSDocumentRecordCreate.renderAsync(
				(pError) =>
				{
					if (pError)
					{
						this.pict.log.error(`Error rendering Document Create view: ${pError}`);
					}
					else
					{
						this.pict.log.info(`Document Create view rendered`);
					}
				});
			});

			this.pictRouter.addRoute('/Sample/:SampleGUID/Edit',
			(pRouteData) =>
			{
				this.pict.log.info(`Samples Edit route called`);

				// Skeleton up front so the page is never an empty frame while the record loads.
				this.pict.PictApplication.showDetailLoadingShell();

				this.currentView = 'Sample/' + pRouteData.data.SampleGUID + '/Edit';
				this.pict.PictApplication.changeTheme('Edit');
				this.pict.views.LIMSSampleRecordEdit.onNavigate(pRouteData.data.SampleGUID);
			});

			this.pictRouter.addRoute('/Printer-Codes',
			(pRouteData) =>
			{
				this.pict.log.info(`Printer Codes route called`);

				this.currentView = 'Printer-Codes';
				this.pict.PictApplication.changeTheme('Edit');
				this.pict.views.LIMSPrinterCodes.renderAsync(
					(pError) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error rendering Printer Codes view: ${pError}`);
						}
						else
						{
							this.pict.log.info(`Printer Codes view rendered`);
						}
					});
			});
			this.pictRouter.addRoute('/SamplePlan-Copy',
			(pRouteData) =>
			{
				this.pict.log.info(`SamplePlan Copy route called`);

				this.currentView = 'SamplePlan-Copy';
				this.pict.PictApplication.changeTheme('Edit');
				this.pict.views.LIMSSamplePlanCopy.renderAsync(
					(pError) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error rendering SamplePlan Copy view: ${pError}`);
						}
						else
						{
							this.pict.log.info(`SamplePlan Copy view rendered`);
						}
					});
			});
			this.pictRouter.addRoute('/Testbed',
			(pRouteData) =>
			{
				this.pict.log.info(`Testbed route called`);

				this.currentView = 'Testbed';
				this.pict.views.LIMSTestbed.renderAsync(
					(pError) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error rendering Testbed view: ${pError}`);
						}
						else
						{
							this.pict.log.info(`Testbed view rendered`);
						}
					});
			});
			return fCallback(pError);
		});
	}
}

module.exports = LIMSRouter;

module.exports.default_configuration = (
	{
		Name: 'LIMSRouter',
	});
